import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const getSessionId = (): string => {
  const key = "funnel_session_id";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
};

const COUNTRY_KEY = "funnel_country";
let countryPromise: Promise<string | null> | null = null;

const getCountry = async (): Promise<string | null> => {
  const cached = sessionStorage.getItem(COUNTRY_KEY);
  if (cached) return cached === "null" ? null : cached;
  if (!countryPromise) {
    countryPromise = (async () => {
      try {
        const res = await fetch("https://api.country.is/", { cache: "force-cache" });
        if (!res.ok) throw new Error("geo failed");
        const json = await res.json();
        const c = typeof json?.country === "string" ? json.country : null;
        sessionStorage.setItem(COUNTRY_KEY, c ?? "null");
        return c;
      } catch {
        sessionStorage.setItem(COUNTRY_KEY, "null");
        return null;
      }
    })();
  }
  return countryPromise;
};

export const trackFunnelStep = async (
  step: string,
  metadata?: Record<string, any>
) => {
  try {
    const sessionId = getSessionId();
    const [{ data: { user } }, country] = await Promise.all([
      supabase.auth.getUser(),
      getCountry(),
    ]);
    await supabase.from("funnel_events").insert({
      session_id: sessionId,
      step,
      page_url: window.location.pathname,
      user_id: user?.id || null,
      metadata: metadata || {},
      country: country,
    });
  } catch (e) {
    // Silent fail - don't block UX for analytics
  }
};

/**
 * Hook to track a funnel step on mount.
 * Only fires once per mount.
 */
export function useFunnelTracker(step: string, metadata?: Record<string, any>) {
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackFunnelStep(step, metadata);
  }, [step]);
}
